'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, NumberedSection, useKiaoConfirm } from '@kiao-voice/ui-kit'
import { clearStoredToken, deleteAccount, OtpApiError } from '@/features/auth/auth-storage'
import { clearRememberedTemplates } from '@/features/batch-transcription/remembered-template'
import { clearHintSuggestionData } from '@/features/dictionary/hint-suggestions'

interface Props {
  /** Test seams. */
  deleteAccountFn?: () => Promise<void>
  clearTokenFn?: () => void
  /** Defaults to a FULL page load (see `goHome`), not `router.push`. */
  navigate?: (path: string) => void
}

/**
 * 刪除帳號 — the self-serve account deletion control shared by the
 * checkout-free `/account-deletion` route (opened by the Android app) and the
 * web account page. The iOS app deletes in-app and does not use this component.
 *
 * Two sequential confirmations guard it: the first states the consequences,
 * the second is the point of no return — a single mis-tap can never delete an
 * account.
 *
 * On success the local session is cleared and the user is sent to the public
 * home page; every one of their tokens is already dead server-side.
 */
export function DeleteAccountSection({
  deleteAccountFn = deleteAccount,
  clearTokenFn = clearStoredToken,
  navigate,
}: Props = {}) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const goHome = useCallback(() => {
    if (navigate) {
      navigate('/')
      return
    }
    // A full page load, NOT router.push: the account no longer exists, so every
    // piece of client state built for it (active context, cached probes, the
    // mounted AuthGuard) has to be thrown away rather than re-rendered.
    window.location.assign('/')
  }, [navigate])

  const handleDelete = useCallback(async () => {
    if (busy) return
    setError(null)

    const acknowledged = await confirm({
      title: t('account.delete.confirmTitle'),
      message: t('account.delete.confirmMessage'),
      confirmText: t('account.delete.confirmCta'),
      cancelText: t('account.delete.cancel'),
    })
    if (!acknowledged) return

    const final = await confirm({
      title: t('account.delete.finalTitle'),
      message: t('account.delete.finalMessage'),
      confirmText: t('account.delete.finalCta'),
      cancelText: t('account.delete.cancel'),
    })
    if (!final) return

    setBusy(true)
    try {
      await deleteAccountFn()
    } catch (cause) {
      reportHandledException(cause, { source: 'app/app/account/delete-account-section.tsx' })
      // Report + visible recovery: the account still exists, so keep the
      // session and tell the user why, in their language. `serverMessage` is
      // never surfaced — every reachable failure has a localized string.
      const blockedByTeam =
        cause instanceof OtpApiError &&
        cause.status === 409 &&
        (cause.body as { error?: string } | undefined)?.error === 'team_has_members'
      if (blockedByTeam) {
        const memberCount = (cause.body as { memberCount?: number }).memberCount ?? 0
        setError(t('account.delete.teamBlocked', { memberCount }))
      } else {
        console.error('[delete-account] deletion failed', cause)
        setError(t('account.delete.failed'))
      }
      setBusy(false)
      return
    }

    // Deleted. Drop the local session before navigating so no guard can
    // re-probe with a token the server already revoked. The frecency hint
    // store and the remembered upload template go with it: a deleted account's
    // hand-typed terms and last-used template must not surface for the
    // browser's next user (`clearStoredToken` deliberately clears neither —
    // that path is also the routine 401 eviction).
    clearTokenFn()
    clearHintSuggestionData()
    clearRememberedTemplates()
    goHome()
  }, [busy, confirm, t, deleteAccountFn, clearTokenFn, goHome])

  return (
    <NumberedSection
      data-testid="account-delete-section"
      title={t('account.delete.heading')}
      description={t('account.delete.desc')}
    >
      <div className="bact">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          data-testid="account-delete-button"
          onClick={() => void handleDelete()}
        >
          {busy ? t('account.delete.deleting') : t('account.delete.button')}
        </Button>
      </div>
      {error && <Alert data-testid="account-delete-error">{error}</Alert>}
    </NumberedSection>
  )
}
